import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";

const EMAIL = "enquiry@norcev.se";

const LINK_KEYS = [
  { key: "services", href: "#services" },
  { key: "process", href: "#process" },
  { key: "results", href: "#results" },
  { key: "about", href: "#about" },
  { key: "contact", href: "#contact" },
];

const scrollTo = (hash) => {
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -72, duration: 1.4 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export const Footer = () => {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success(t.footer.copied);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t.footer.copyFailed, { description: EMAIL });
    }
  };

  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-[#0B1220] text-slate-300">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-12">
        <div>
          <div className="flex items-center gap-3">
            <Logo light />
          <span className="font-display text-lg font-bold tracking-tight text-white">
            NORCEV
          </span>
          </div>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">{t.footer.blurb}</p>
          <button
            data-testid="footer-copy-email"
            onClick={copyEmail}
            className="font-mono-alt mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-slate-300 transition-all duration-300 hover:border-teal-400/60 hover:text-teal-300"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {EMAIL}
          </button>
        </div>

        <div>
          <div className="font-mono-alt mb-5 text-xs uppercase tracking-[0.25em] text-slate-500">{t.footer.navigate}</div>
          <div className="flex flex-col gap-3">
            {LINK_KEYS.map((l) => (
              <button
                key={l.href}
                data-testid={`footer-link-${l.key}`}
                onClick={() => scrollTo(l.href)}
                className="w-fit text-sm text-slate-400 transition-colors duration-300 hover:text-teal-300"
              >
                {t.nav[l.key]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="font-mono-alt mb-5 text-xs uppercase tracking-[0.25em] text-slate-500">{t.footer.offices}</div>
          <div className="space-y-2 text-sm text-slate-400">
            {t.footer.officeList.map((o, i) => (
              <p key={o} data-testid={`footer-office-${i}`}>{o}</p>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="font-mono-alt mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-[10px] uppercase tracking-[0.2em] text-slate-500 sm:px-6 lg:px-12">
          <span>{t.footer.rights}</span>
          <span>{t.footer.placeholder}</span>
        </div>
      </div>
    </footer>
  );
};
